<template>
  <div class="max-w-6xl w-full mx-auto px-4 space-y-6">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
        <p class="text-sm text-gray-600">{{ pageSubtitle }}</p>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          v-if="isProblems && failedPhotos.length"
          type="button"
          class="px-3 py-2 rounded-md border bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm"
          :disabled="bulkRetrying"
          @click="bulkRetryFailed"
        >
          {{ bulkRetrying ? 'Retrying…' : `Retry failed (${failedPhotos.length})` }}
        </button>
        <button
          v-if="isProblems"
          type="button"
          class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50 text-sm"
          @click="$router.push('/admin/snapshot-bulk')"
        >
          Upload photos
        </button>
        <button
          v-else
          type="button"
          class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50 text-sm"
          @click="$router.push('/admin/snapshot-problems')"
        >
          View problems
        </button>
      </div>
    </div>

    <div v-if="!isProblems" class="bg-white border rounded-lg p-4">
      <label class="block text-sm font-medium text-gray-700 mb-1">Ceremony / photo set ID</label>
      <div class="flex flex-wrap gap-2">
        <input
          v-model.trim="collectionInput"
          class="flex-1 min-w-[260px] px-3 py-2 border rounded-md"
          placeholder="Paste a ceremony or photo set ID"
          @keyup.enter="goToCollection"
        />
        <button
          type="button"
          class="px-4 py-2 rounded-md border bg-gray-900 text-white"
          :disabled="!collectionInput"
          @click="goToCollection"
        >
          Open photo set
        </button>
      </div>
    </div>

    <div v-if="error" class="rounded-md border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
      {{ error }}
    </div>
    <div v-if="notice" class="rounded-md border border-green-200 bg-green-50 text-green-800 px-4 py-3 text-sm">
      {{ notice }}
    </div>

    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div class="bg-white border rounded-lg p-3">
        <div class="text-xs text-gray-500">Loaded</div>
        <div class="text-2xl font-semibold text-gray-900">{{ photos.length }}</div>
      </div>
      <div class="bg-white border rounded-lg p-3">
        <div class="text-xs text-gray-500">Ready</div>
        <div class="text-2xl font-semibold text-green-700">{{ statusCount('ready') }}</div>
      </div>
      <div class="bg-white border rounded-lg p-3">
        <div class="text-xs text-gray-500">Stored / indexed</div>
        <div class="text-2xl font-semibold text-blue-700">{{ statusCount('stored') + statusCount('indexed') }}</div>
      </div>
      <div class="bg-white border rounded-lg p-3">
        <div class="text-xs text-gray-500">Failed / hidden</div>
        <div class="text-2xl font-semibold text-red-700">{{ statusCount('failed') + statusCount('hidden') }}</div>
      </div>
    </div>

    <div class="bg-white border rounded-lg overflow-hidden">
      <div v-if="loading && photos.length === 0" class="p-8 text-center text-sm text-gray-600">
        Loading photos…
      </div>
      <div v-else-if="photos.length === 0" class="p-8 text-center text-sm text-gray-600">
        No photos found.
      </div>
      <ul v-else class="divide-y">
        <li
          v-for="photo in photos"
          :key="photo.photo_id"
          class="p-3 md:p-4 flex gap-4 hover:bg-gray-50 cursor-pointer"
          @click="openPhoto(photo)"
        >
          <div class="w-24 h-20 bg-gray-100 rounded-md border overflow-hidden flex-shrink-0">
            <img
              v-if="thumbnailUrl(photo)"
              :src="thumbnailUrl(photo)"
              :alt="photo.photo_id"
              class="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap gap-2 items-center">
              <div class="font-medium text-gray-900 truncate" :title="photo.photo_id">
                {{ photo.file_label || 'image' }}
              </div>
              <span class="text-xs rounded-full px-2 py-0.5 border" :class="statusClass(photo.status)">
                {{ photo.status || 'unknown' }}
              </span>
              <span class="text-xs rounded-full px-2 py-0.5 bg-gray-100 text-gray-700 border border-gray-200">
                {{ photo.source || 'unknown' }}
              </span>
            </div>
            <div class="mt-1 text-xs text-gray-500 font-mono truncate">{{ photo.photo_id }}</div>
            <div class="mt-2 grid md:grid-cols-3 gap-1 text-xs text-gray-600">
              <div>Created: {{ formatDate(photo.created_at) }}</div>
              <div>Updated: {{ formatDate(photo.updated_at) }}</div>
              <div>Faces: {{ faceCount(photo) }}</div>
            </div>
            <div v-if="photo.failure_reason" class="mt-2 text-xs text-red-700 truncate">
              {{ photo.failure_reason }}
            </div>
          </div>
        </li>
      </ul>
    </div>

    <div class="flex justify-center">
      <button
        v-if="cursor"
        type="button"
        class="px-4 py-2 rounded-md border bg-white hover:bg-gray-50"
        :disabled="loading"
        @click="loadMore"
      >
        {{ loading ? 'Loading…' : 'Load more' }}
      </button>
    </div>

    <div v-if="selectedPhoto" class="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end" @click.self="closePhoto">
      <aside class="w-full max-w-xl h-full bg-white shadow-xl overflow-auto">
        <div class="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 class="font-semibold text-gray-900">Photo details</h2>
          <button class="px-2 py-1 rounded border bg-white hover:bg-gray-50" @click="closePhoto">Close</button>
        </div>
        <div class="p-4 space-y-4">
          <img
            v-if="thumbnailUrl(selectedPhoto)"
            :src="thumbnailUrl(selectedPhoto)"
            :alt="selectedPhoto.photo_id"
            class="w-full max-h-80 object-contain bg-gray-100 rounded border"
          />
          <div class="flex flex-wrap gap-2">
            <button
              v-if="selectedPhoto.status === 'failed'"
              type="button"
              class="px-3 py-2 rounded-md border bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm"
              :disabled="updatingStatus"
              @click="updatePhotoStatus('retry')"
            >
              Retry processing
            </button>
            <button
              v-if="selectedPhoto.status !== 'hidden' && selectedPhoto.status !== 'deleted'"
              type="button"
              class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50 text-sm"
              :disabled="updatingStatus"
              @click="updatePhotoStatus('hide')"
            >
              Hide photo
            </button>
            <button
              v-if="selectedPhoto.status === 'hidden' || selectedPhoto.status === 'deleted'"
              type="button"
              class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50 text-sm"
              :disabled="updatingStatus"
              @click="updatePhotoStatus('restore')"
            >
              Restore photo
            </button>
            <button
              v-if="selectedPhoto.status !== 'deleted'"
              type="button"
              class="px-3 py-2 rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-sm"
              :disabled="updatingStatus"
              @click="updatePhotoStatus('delete')"
            >
              Remove from gallery
            </button>
          </div>
          <dl class="grid grid-cols-1 gap-3 text-sm">
            <div v-for="field in detailFields" :key="field.key">
              <dt class="text-xs uppercase tracking-wide text-gray-500">{{ field.label }}</dt>
              <dd class="mt-1 break-words" :class="field.mono ? 'font-mono text-xs' : 'text-gray-900'">
                {{ field.value || '—' }}
              </dd>
            </div>
          </dl>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { trackEvent } from '../../Analytics.vue'

export default {
  name: 'SnapshotPhotoAdmin',
  data() {
    return {
      photos: [],
      cursor: null,
      loading: false,
      error: '',
      selectedPhoto: null,
      updatingStatus: false,
      bulkRetrying: false,
      notice: '',
      collectionInput: this.$route.params.photoSetId || this.$route.params.collectionId || '',
    }
  },
  computed: {
    isProblems() {
      return this.$route.name === 'snapshotProblems'
    },
    collectionId() {
      return this.$route.params.photoSetId || this.$route.params.collectionId || ''
    },
    pageTitle() {
      return this.isProblems ? 'Snapshot Problems' : 'Snapshot Photo Set'
    },
    pageSubtitle() {
      if (this.isProblems) return 'Failed and hidden photos that need review.'
      return this.collectionId ? `Review photos in ${this.collectionId}.` : 'Enter a ceremony or photo set ID to review photos.'
    },
    detailFields() {
      const p = this.selectedPhoto || {}
      return [
        { key: 'photo_id', label: 'Photo ID', value: p.photo_id, mono: true },
        { key: 'file_label', label: 'File', value: p.file_label || 'image' },
        { key: 'status', label: 'Status', value: p.status },
        { key: 'source', label: 'Source', value: p.source },
        { key: 'collection_id', label: 'Photo set', value: p.collection_id, mono: true },
        { key: 'face_count', label: 'Faces', value: this.faceCount(p) },
        { key: 'failure_reason', label: 'Failure reason', value: p.failure_reason },
        { key: 'created_at', label: 'Created', value: this.formatDate(p.created_at) },
        { key: 'updated_at', label: 'Updated', value: this.formatDate(p.updated_at) },
      ]
    },
    failedPhotos() {
      return this.photos.filter((photo) => photo.status === 'failed')
    },
  },
  watch: {
    '$route.fullPath'() {
      this.collectionInput = this.collectionId
      this.resetAndLoad()
    },
  },
  created() {
    this.resetAndLoad()
  },
  methods: {
    apiBase() {
      return process.env.VUE_APP_SNAPSHOT_API_BASE || ''
    },
    resetAndLoad() {
      this.photos = []
      this.cursor = null
      this.error = ''
      this.notice = ''
      this.selectedPhoto = null
      if (!this.isProblems && !this.collectionId) return
      this.fetchPage()
      trackEvent(this.$route.path, {
        label: this.isProblems ? 'SNAPSHOT_ADMIN_PROBLEMS_OPENED' : 'SNAPSHOT_ADMIN_GALLERY_OPENED',
        collection_id: this.collectionId || undefined,
      })
    },
    endpoint(cursor) {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
      if (this.isProblems) return `${this.apiBase()}/admin/snapshot/problems${qs}`
      return `${this.apiBase()}/admin/snapshot/photo-sets/${this.collectionId}/photos${qs}`
    },
    async fetchPage(cursor = null) {
      if (this.loading) return
      this.loading = true
      this.error = ''
      this.notice = ''
      try {
        const r = await axios.get(this.endpoint(cursor))
        const items = r.data.items || []
        this.photos = cursor ? this.photos.concat(items) : items
        this.cursor = r.data.cursor || null
      } catch (e) {
        this.error = 'Unable to load snapshot photos.'
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      const cursor = this.cursor
      if (!cursor) return
      trackEvent(this.$route.path, {
        label: 'SNAPSHOT_ADMIN_GALLERY_LOAD_MORE',
        collection_id: this.collectionId || undefined,
        problems: this.isProblems,
      })
      this.fetchPage(cursor)
    },
    goToCollection() {
      if (!this.collectionInput) return
      this.$router.push(`/admin/snapshot-photo-sets/${this.collectionInput}/photos`)
    },
    async openPhoto(photo) {
      this.selectedPhoto = photo
      trackEvent(this.$route.path, {
        label: 'SNAPSHOT_ADMIN_PHOTO_OPENED',
        photo_id: photo.photo_id,
        collection_id: photo.collection_id,
      })
      try {
        const r = await axios.get(`${this.apiBase()}/admin/snapshot/photos/${photo.photo_id}`)
        this.selectedPhoto = r.data || photo
      } catch (e) {
        this.selectedPhoto = photo
      }
    },
    closePhoto() {
      this.selectedPhoto = null
    },
    async updatePhotoStatus(action) {
      if (!this.selectedPhoto || this.updatingStatus) return
      this.updatingStatus = true
      this.error = ''
      try {
        const r = await axios.patch(
          `${this.apiBase()}/admin/snapshot/photos/${this.selectedPhoto.photo_id}/status`,
          { action }
        )
        const updated = r.data
        this.selectedPhoto = updated
        this.photos = this.photos.map((photo) => (
          photo.photo_id === updated.photo_id ? { ...photo, ...updated } : photo
        ))
        trackEvent(this.$route.path, {
          label: 'SNAPSHOT_ADMIN_PHOTO_STATUS_UPDATED',
          photo_id: updated.photo_id,
          collection_id: updated.collection_id,
          action,
        })
      } catch (e) {
        this.error = 'Unable to update photo status.'
      } finally {
        this.updatingStatus = false
      }
    },
    async bulkRetryFailed() {
      if (!this.failedPhotos.length || this.bulkRetrying) return
      this.bulkRetrying = true
      this.error = ''
      this.notice = ''
      let retried = 0
      let failed = 0
      const failedPhotos = [...this.failedPhotos]
      for (const photo of failedPhotos) {
        try {
          const r = await axios.patch(
            `${this.apiBase()}/admin/snapshot/photos/${photo.photo_id}/status`,
            { action: 'retry' }
          )
          const updated = r.data
          this.photos = this.photos.map((item) => (
            item.photo_id === updated.photo_id ? { ...item, ...updated } : item
          ))
          if (this.selectedPhoto && this.selectedPhoto.photo_id === updated.photo_id) {
            this.selectedPhoto = { ...this.selectedPhoto, ...updated }
          }
          retried += 1
        } catch (e) {
          failed += 1
        }
      }
      this.bulkRetrying = false
      if (retried) {
        this.notice = failed
          ? `Retry requested for ${retried} failed photo${retried === 1 ? '' : 's'}; ${failed} could not be retried.`
          : `Retry requested for ${retried} failed photo${retried === 1 ? '' : 's'}.`
      }
      if (!retried && failed) {
        this.error = 'Unable to retry the selected failed photos.'
      }
      trackEvent(this.$route.path, {
        label: 'SNAPSHOT_ADMIN_BULK_RETRY',
        retried,
        failed,
      })
    },
    thumbnailUrl(photo) {
      if (photo && photo.thumb_url) return photo.thumb_url
      return ''
    },
    statusCount(status) {
      return this.photos.filter((p) => p.status === status).length
    },
    faceCount(photo) {
      return Number(photo && photo.face_count ? photo.face_count : 0)
    },
    statusClass(status) {
      if (status === 'ready') return 'bg-green-50 text-green-700 border-green-200'
      if (status === 'failed') return 'bg-red-50 text-red-700 border-red-200'
      if (status === 'hidden') return 'bg-yellow-50 text-yellow-700 border-yellow-200'
      return 'bg-blue-50 text-blue-700 border-blue-200'
    },
    formatDate(value) {
      if (!value) return ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
  },
}
</script>
